import { Link, useLocation, useNavigate } from "react-router";
import { confirmSignOut } from "../components/confirmations";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { useMe, useAuth } from "../features/auth/AuthProvider";
import { useUnread } from "../lib/hooks";
import { useUI, type Theme } from "../lib/uiStore";
import { cn } from "../lib/cn";
import { setTheme, usePrefs } from "../lib/prefs";
import { comboLabel, isSingleKey } from "../features/settings/keybinds";
import { NAV_ITEMS, type KeybindAction, type NavItem, type Preferences } from "@atrium/shared";
import { PresenceAvatar } from "../components/PresenceIndicator";
import { PresenceIndicator } from "../components/PresenceIndicator";
import { presenceActions, STATUS_LABELS } from "../lib/presence";
import type { PresenceStatusChoice } from "@atrium/shared";
import { Tooltip } from "../components/ui/Tooltip";
import { Badge } from "../components/ui/Controls";
import { Menu, MenuContent, MenuItem, MenuLabel, MenuSeparator, MenuTrigger } from "../components/ui/Menu";
import { LogoMark } from "../components/Logo";
import { RailCommunities } from "../features/rail/RailCommunities";
import {
  IconBell,
  IconBellActive,
  IconBookmark,
  IconBookmarkFilled,
  IconCommand,
  IconKeyboard,
  IconSearch,
  IconSmile,
  IconCommunities,
  IconCommunitiesActive,
  IconCompose,
  IconExplore,
  IconExploreActive,
  IconFeed,
  IconFeedActive,
  IconLogout,
  IconMessages,
  IconMessagesActive,
  IconMonitor,
  IconMoon,
  IconSettings,
  IconShieldCheck,
  IconSun,
  IconUser,
} from "../components/icons";
import { IconStaffShield } from "../components/socialIcons";
import { isPlatformStaff } from "@atrium/shared";

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

/*
 * Primary navigation entries. The rail and the mobile tab bar render them in the
 * user's order (prefs.navOrder) without the hidden ones (prefs.navHidden).
 */
export interface NavDef {
  label: string;
  icon: Icon;
  activeIcon: Icon;
  action?: KeybindAction;
  isActive: (pathname: string) => boolean;
}

export const NAV: Record<NavItem, NavDef> = {
  communities: { label: "Communities", icon: IconCommunities, activeIcon: IconCommunitiesActive, action: "goCommunities", isActive: (p) => p === "/c" || p.startsWith("/c/") },
  feed: { label: "Feed", icon: IconFeed, activeIcon: IconFeedActive, action: "goFeed", isActive: (p) => p === "/feed" },
  explore: { label: "Explore", icon: IconExplore, activeIcon: IconExploreActive, action: "goExplore", isActive: (p) => p.startsWith("/explore") || p.startsWith("/t/") },
  messages: { label: "Messages", icon: IconMessages, activeIcon: IconMessagesActive, action: "goMessages", isActive: (p) => p.startsWith("/messages") },
  notifications: { label: "Notifications", icon: IconBell, activeIcon: IconBellActive, action: "goNotifications", isActive: (p) => p.startsWith("/notifications") },
  saved: { label: "Saved", icon: IconBookmark, activeIcon: IconBookmarkFilled, isActive: (p) => p.startsWith("/saved") },
};

/** The user's order (unknown or missing items appended), optionally without hidden ones. */
export function orderedNav(p: Pick<Preferences, "navOrder" | "navHidden">, includeHidden = false): NavItem[] {
  const order = [...new Set([...p.navOrder.filter((i) => NAV_ITEMS.includes(i)), ...NAV_ITEMS])];
  return includeHidden ? order : order.filter((i) => !p.navHidden.includes(i));
}

/** Where a nav entry goes, restoring the last place within its section. */
export function useNavTarget() {
  const { lastCommunityPath, lastMessagesPath, lastExplorePath } = useUI();
  return (item: NavItem, pathname: string): string => {
    switch (item) {
      case "communities":
        return pathname.startsWith("/c/") ? "/c" : lastCommunityPath || "/c";
      case "messages":
        return pathname.startsWith("/messages/") ? "/messages" : lastMessagesPath || "/messages";
      case "explore":
        return pathname.startsWith("/explore") ? "/explore" : lastExplorePath || "/explore";
      default:
        return `/${item}`;
    }
  };
}

function RailButton({
  label,
  icon: I,
  activeIcon: A,
  active,
  onClick,
  badge,
  shortcut,
}: {
  label: string;
  icon: Icon;
  activeIcon: Icon;
  active: boolean;
  onClick: () => void;
  badge?: ReactNode;
  shortcut?: string;
}) {
  const Cmp = active ? A : I;
  return (
    <Tooltip label={label} side="right" shortcut={shortcut}>
      <button
        onClick={onClick}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        className={cn(
          "relative flex size-11 items-center justify-center rounded-xl transition-colors duration-150",
          active ? "bg-surface-2 text-fg" : "text-fg-3 hover:bg-surface hover:text-fg",
        )}
      >
        <Cmp className="icon size-[22px]" aria-hidden />
        {badge && <span className="absolute -right-0.5 -top-0.5">{badge}</span>}
      </button>
    </Tooltip>
  );
}

const STATUS_CHOICES: PresenceStatusChoice[] = ["online", "idle", "dnd", "invisible"];
const STATUS_ICONS = Object.fromEntries(
  STATUS_CHOICES.map((c) => [c, (p: SVGProps<SVGSVGElement>) => <PresenceIndicator status={c === "invisible" ? "offline" : c} size="md" label={false} className={cn(p.className, "[--presence-ring:transparent]")} />]),
) as Record<PresenceStatusChoice, Icon>;

export function ThemeMenuItems() {
  const { theme } = usePrefs();
  const items: { value: Theme; label: string; icon: Icon }[] = [
    { value: "dark", label: "Dark", icon: IconMoon },
    { value: "light", label: "Light", icon: IconSun },
    { value: "system", label: "System", icon: IconMonitor },
  ];
  return (
    <>
      <MenuLabel>Theme</MenuLabel>
      {items.map((i) => (
        <MenuItem key={i.value} icon={i.icon} onSelect={() => void setTheme(i.value)} hint={theme === i.value ? "Current" : undefined}>
          {i.label}
        </MenuItem>
      ))}
    </>
  );
}

export function Rail() {
  const me = useMe();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { data: unread } = useUnread();
  const { openComposer, setOverlay } = useUI();
  const target = useNavTarget();
  const activeCommunityId = /^\/c\/([^/]+)/.exec(pathname)?.[1];
  const prefs = usePrefs();
  /** Tooltip hint for an action's shortcut, hidden when unset or disabled. */
  const hint = (a: KeybindAction) => {
    const combo = prefs.keybinds[a];
    if (!combo || (!prefs.singleKeyShortcuts && isSingleKey(combo))) return undefined;
    return comboLabel(combo);
  };

  return (
    <nav aria-label="Primary" className="sidebar-surface flex h-full w-[72px] shrink-0 flex-col items-center border-r py-3">
      <Link to="/feed" aria-label="Atrium home" className="mb-3 flex size-11 items-center justify-center rounded-xl text-fg hover:bg-surface">
        <LogoMark className="size-7" />
      </Link>

      <div className="flex flex-col items-center gap-1">
        {orderedNav(prefs).map((item) => {
          const def = NAV[item];
          const badge = item === "messages" ? unread?.conversations : item === "notifications" ? unread?.notifications : undefined;
          return (
            <RailButton
              key={item}
              label={def.label}
              shortcut={def.action ? hint(def.action) : undefined}
              icon={def.icon}
              activeIcon={def.activeIcon}
              active={def.isActive(pathname)}
              onClick={() => navigate(target(item, pathname))}
              badge={badge ? <Badge count={badge} /> : undefined}
            />
          );
        })}
      </div>

      <div className="my-3 h-px w-8 shrink-0 bg-line" />

      <RailCommunities activeCommunityId={activeCommunityId} />

      <div className="flex flex-col items-center gap-2 pt-2">
        {isPlatformStaff(me.platformRole) && (
          <RailButton label="Staff console" icon={IconShieldCheck} activeIcon={IconStaffShield} active={pathname.startsWith("/staff")} onClick={() => navigate("/staff")} />
        )}
        <Tooltip label="New post" side="right" shortcut={hint("newPost")}>
          <button
            onClick={() => openComposer()}
            aria-label="New post"
            className="flex size-11 items-center justify-center rounded-xl text-fg-3 transition-colors duration-150 hover:bg-surface hover:text-fg"
          >
            <IconCompose className="icon size-[22px]" />
          </button>
        </Tooltip>
        <Menu>
          <Tooltip label="Account" side="right">
            <MenuTrigger asChild>
              <button aria-label="Account menu" className="rounded-full p-1 hover:bg-surface">
                <PresenceAvatar userId={me.id} src={me.avatarUrl} name={me.displayName} size="md" />
              </button>
            </MenuTrigger>
          </Tooltip>
          <MenuContent side="right" align="end">
            <div className="px-2.5 py-2">
              <div className="truncate text-sm font-semibold text-fg">{me.displayName}</div>
              <div className="truncate text-xs text-fg-3">@{me.username}</div>
            </div>
            <MenuSeparator />
            <MenuLabel>Status</MenuLabel>
            {STATUS_CHOICES.map((c) => (
              <MenuItem key={c} icon={STATUS_ICONS[c]} onSelect={() => void presenceActions.setStatus(c)} hint={me.presence.status === c ? "Current" : undefined}>
                {STATUS_LABELS[c]}
              </MenuItem>
            ))}
            <MenuItem icon={IconSmile} onSelect={() => setOverlay("status")}>
              {me.presence.customStatus ? "Edit custom status" : "Set a custom status"}
            </MenuItem>
            <MenuSeparator />
            <MenuItem icon={IconUser} onSelect={() => navigate(`/u/${me.username}`)}>
              Profile
            </MenuItem>
            <MenuItem icon={IconSearch} onSelect={() => navigate("/search")} hint={hint("search")}>
              Search
            </MenuItem>
            <MenuItem icon={IconCommand} onSelect={() => setOverlay("palette")} hint={hint("commandPalette")}>
              Command palette
            </MenuItem>
            <MenuItem icon={IconKeyboard} onSelect={() => setOverlay("shortcuts")} hint={hint("shortcutHelp")}>
              Keyboard shortcuts
            </MenuItem>
            <MenuItem icon={IconSettings} onSelect={() => navigate("/settings")} hint={hint("goSettings")}>
              Settings
            </MenuItem>
            <MenuSeparator />
            <ThemeMenuItems />
            <MenuSeparator />
            <MenuItem icon={IconLogout} onSelect={async () => (await confirmSignOut()) && void logout()}>
              Sign out
            </MenuItem>
          </MenuContent>
        </Menu>
      </div>
    </nav>
  );
}
